import type { Command, EditorState, Transaction } from 'prosemirror-state';
import { wrapInList, liftListItem } from 'prosemirror-schema-list';
import { CellSelection, cellAround, isInTable, addColumnAfter, addRowAfter, addColumnBefore, addRowBefore, deleteRow, mergeCells, splitCell, deleteColumn } from 'prosemirror-tables';
import { manualSchema } from './schema';

function insertNode(node: import('prosemirror-model').Node): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const { $from } = state.selection;
    const index = $from.index();
    if (!$from.parent.canReplaceWith(index, index, node.type)) return false;
    if (dispatch) {
      const tr = state.tr.replaceSelectionWith(node);
      dispatch(tr);
    }
    return true;
  };
}

export function insertInlineXamix(textInput: string, bold: boolean = false, brush: boolean = false, color: string | null = null): Command {
  const node = manualSchema.nodes.xamix.create({ textInput, bold, brush, color });
  return insertNode(node);
}

export const wrapBulletList = wrapInList(manualSchema.nodes.bullet_list);
export const wrapOrderedList = wrapInList(manualSchema.nodes.ordered_list);
export const liftListItemCmd = liftListItem(manualSchema.nodes.list_item);

export function insertHardBreak(): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    if (dispatch) {
      dispatch(state.tr.replaceSelectionWith(manualSchema.nodes.hard_break.create()).scrollIntoView());
    }
    return true;
  };
}

export function insertInlineImage(src: string, alt: string = '', title: string = '', width: number | null = null, height: number | null = null): Command {
  const node = manualSchema.nodes.inline_image.create({ src, alt, title, width, height });
  return insertNode(node);
}

export function updateInlineImageAttrs(pos: number, attrs: { width?: number | null; height?: number | null; alt?: string; title?: string }): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const node = state.doc.nodeAt(pos);
    if (!node || node.type.name !== 'inline_image') return false;
    if (dispatch) {
      dispatch(state.tr.setNodeMarkup(pos, null, { ...node.attrs, ...attrs }));
    }
    return true;
  };
}

export function insertInlineSvg(content: string, width: number | null = null, height: number | null = null): Command {
  const node = manualSchema.nodes.inline_svg.create({ content, width, height });
  return insertNode(node);
}

export function updateInlineSvgAttrs(pos: number, attrs: { width?: number | null; height?: number | null }): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const node = state.doc.nodeAt(pos);
    if (!node || node.type.name !== 'inline_svg') return false;
    if (dispatch) {
      dispatch(state.tr.setNodeMarkup(pos, null, { ...node.attrs, ...attrs }));
    }
    return true;
  };
}

export function insertPageBreak(): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const { $from } = state.selection;
    const pos = $from.after();
    const node = manualSchema.nodes.page_break.create();
    const tr = state.tr.insert(pos, node);
    if (dispatch) dispatch(tr);
    return true;
  };
}

export function insertBlockXamix(textInput: string, level: 0 | 1 | 2 | 3 = 0, bold: boolean = false, brush: boolean = false, color: string | null = null): Command {
  const node = manualSchema.nodes.block_xamix.create({ textInput, level, bold, brush, color });
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const { $from } = state.selection;
    const pos = $from.after();
    const tr = state.tr.insert(pos, node);
    tr.setSelection(state.selection.constructor.near(tr.doc.resolve(pos + 1)) as any);
    if (dispatch) dispatch(tr);
    return true;
  };
}

/** Insert a new table with the given number of rows and columns at the current selection. */
export function insertTable(rows: number, cols: number, margin: string = '', backgroundColor: string = '', borderColor: string = ''): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const { table, table_row, table_cell } = manualSchema.nodes;
    if (!table || !table_row || !table_cell) return false;
    const cellContent = manualSchema.nodes.paragraph.create(null, manualSchema.text('\u200B'));
    const cells = Array.from({ length: cols }, () => table_cell.create(null, cellContent));
    const tableRows = Array.from({ length: rows }, () => table_row.create(null, [...cells]));
    const tableNode = table.create({ margin: margin || '', backgroundColor: backgroundColor || '', borderColor: borderColor || '' }, tableRows);
    const { $from } = state.selection;
    const pos = $from.after();
    const tr = state.tr.insert(pos, tableNode);
    if (dispatch) dispatch(tr);
    return true;
  };
}

/** Get the table node and its position that contains the current selection, or null. */
export function getTableAtSelection(state: EditorState): { node: import('prosemirror-model').Node; pos: number } | null {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.spec.tableRole === 'table') {
      return { node, pos: $from.before(d) };
    }
  }
  return null;
}

/** Get the margin attribute of the table at the current selection, or null if not in a table. */
export function getTableMargin(state: EditorState): string | null {
  const at = getTableAtSelection(state);
  if (!at) return null;
  const margin = at.node.attrs.margin;
  return margin != null && margin !== '' ? margin : null;
}

/** Set margin on the table containing the current selection. */
export function setTableMargin(margin: string): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const at = getTableAtSelection(state);
    if (!at) return false;
    if (dispatch) {
      dispatch(state.tr.setNodeMarkup(at.pos, null, { ...at.node.attrs, margin: margin || '' }));
    }
    return true;
  };
}

/** Get the table background color at the current selection, or null. */
export function getTableBackgroundColor(state: EditorState): string | null {
  const at = getTableAtSelection(state);
  if (!at) return null;
  const bg = at.node.attrs.backgroundColor;
  return bg != null && bg !== '' ? bg : null;
}

/** Get the table border color at the current selection, or null. */
export function getTableBorderColor(state: EditorState): string | null {
  const at = getTableAtSelection(state);
  if (!at) return null;
  const c = at.node.attrs.borderColor;
  return c != null && c !== '' ? c : null;
}

/** Set border color on the table containing the current selection. */
export function setTableBorderColor(color: string): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const at = getTableAtSelection(state);
    if (!at) return false;
    if (dispatch) {
      dispatch(state.tr.setNodeMarkup(at.pos, null, { ...at.node.attrs, borderColor: color || '' }));
    }
    return true;
  };
}

/** Set background color on the table containing the current selection. */
export function setTableBackgroundColor(color: string): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const at = getTableAtSelection(state);
    if (!at) return false;
    if (dispatch) {
      dispatch(state.tr.setNodeMarkup(at.pos, null, { ...at.node.attrs, backgroundColor: color || '' }));
    }
    return true;
  };
}

/** Add a column after the current cell in the table. */
export function addColumnAfterCmd(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!isInTable(state)) return false;
  if (dispatch) {
    const result = addColumnAfter(state, (tr) => {
      // After adding column, update colWidths if table has them
      // Find the table from the new document
      let foundTable = false;
      tr.doc.descendants((node) => {
        if (foundTable || node.type.name !== 'table') return;
        const colWidths = (node.attrs as { colWidths?: number[] }).colWidths;
        if (colWidths && Array.isArray(colWidths)) {
          // Add a default width for the new column (use average of existing or default)
          const avgWidth = colWidths.reduce((a, b) => a + b, 0) / colWidths.length || 100;
          const newColWidths = [...colWidths, Math.round(avgWidth)];
          tr.setNodeMarkup(tr.mapping.map(state.selection.$from.before()), null, { ...node.attrs, colWidths: newColWidths });
          foundTable = true;
        }
      });
      dispatch(tr);
    });
    return result;
  }
  return addColumnAfter(state, dispatch);
}

/** Add a column before the current cell in the table. */
export function addColumnBeforeCmd(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!isInTable(state)) return false;
  if (dispatch) {
    const result = addColumnBefore(state, (tr) => {
      // After adding column, update colWidths if table has them
      // Find the table from the new document
      let foundTable = false;
      tr.doc.descendants((node) => {
        if (foundTable || node.type.name !== 'table') return;
        const colWidths = (node.attrs as { colWidths?: number[] }).colWidths;
        if (colWidths && Array.isArray(colWidths)) {
          // Add a default width for the new column (use average of existing or default)
          const avgWidth = colWidths.reduce((a, b) => a + b, 0) / colWidths.length || 100;
          const newColWidths = [Math.round(avgWidth), ...colWidths];
          tr.setNodeMarkup(tr.mapping.map(state.selection.$from.before()), null, { ...node.attrs, colWidths: newColWidths });
          foundTable = true;
        }
      });
      dispatch(tr);
    });
    return result;
  }
  return addColumnBefore(state, dispatch);
}

/** Add a row after the current cell in the table. */
export const addRowAfterCmd = addRowAfter;

/** Add a row before the current cell in the table. */
export const addRowBeforeCmd = addRowBefore;

/** Remove the column containing the current cell. */
export function removeColumnCmd(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!isInTable(state)) return false;

  return (deleteColumn as any)(state, dispatch);
}

/** Remove the row containing the current cell. */
export function removeRowCmd(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!isInTable(state)) return false;

  return (deleteRow as any)(state, dispatch);
}

/** Merge the selected cells into a single cell. */
export function mergeCellsCmd(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!isInTable(state)) return false;
  return mergeCells(state, dispatch);
}

/** Split the selected merged cell into multiple cells. */
export function splitCellCmd(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
  if (!isInTable(state)) return false;
  return splitCell(state, dispatch);
}

/** Find xamix or block_xamix node at selection, or null. */
function getXamixAtSelection(state: EditorState): { node: import('prosemirror-model').Node; pos: number } | null {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'block_xamix') {
      return { node, pos: $from.before(d) };
    }
  }
  const nodeBefore = $from.nodeBefore;
  const nodeAfter = $from.nodeAfter;
  if (nodeBefore?.type.name === 'xamix') {
    return { node: nodeBefore, pos: $from.pos - nodeBefore.nodeSize };
  }
  if (nodeAfter?.type.name === 'xamix') {
    return { node: nodeAfter, pos: $from.pos };
  }
  return null;
}

/** Get text color from the current selection (text mark, highlight mark's textColor, or xamix/block_xamix attr). */
export function getTextColor(state: EditorState): string | null {
  const xamix = getXamixAtSelection(state);
  if (xamix) {
    const c = (xamix.node.attrs as { color?: string | null }).color;
    return c && c !== '' ? c : null;
  }
  const { $from } = state.selection;
  // First check for textColor mark
  const textColorMark = $from.marks().find((m) => m.type.name === 'textColor');
  if (textColorMark) return (textColorMark.attrs as { color?: string }).color ?? null;
  // Also check highlight mark's textColor attribute (when both color and highlight are present)
  const highlightMark = $from.marks().find((m) => m.type.name === 'highlight');
  if (highlightMark) {
    const textColor = (highlightMark.attrs as { textColor?: string | null }).textColor;
    if (textColor && textColor !== '') return textColor;
  }
  const { from, to } = state.selection;
  let found: string | null = null;
  state.doc.nodesBetween(from, to, (n) => {
    // Check for textColor mark
    const m = n.marks.find((mk) => mk.type.name === 'textColor');
    if (m) found = (m.attrs as { color?: string }).color ?? null;
    // Also check highlight mark's textColor
    const hl = n.marks.find((mk) => mk.type.name === 'highlight');
    if (hl && !found) {
      const hlTextColor = (hl.attrs as { textColor?: string | null }).textColor;
      if (hlTextColor && hlTextColor !== '') found = hlTextColor;
    }
  });
  return found;
}

/** Get highlight color from the current selection. */
export function getHighlightColor(state: EditorState): string | null {
  const { $from } = state.selection;
  const mark = $from.marks().find((m) => m.type.name === 'highlight');
  if (mark) return (mark.attrs as { color?: string }).color ?? null;
  const { from, to } = state.selection;
  let found: string | null = null;
  state.doc.nodesBetween(from, to, (n) => {
    const m = n.marks.find((mk) => mk.type.name === 'highlight');
    if (m) found = (m.attrs as { color?: string }).color ?? null;
  });
  return found;
}

/** Set highlight color on the current selection. Pass '' to remove. */
export function setHighlightColor(color: string): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const { from, to } = state.selection;
    if (!color) {
      if (dispatch) {
        dispatch(state.tr.removeMark(from, to, state.schema.marks.highlight));
      }
      return true;
    }
    // Get current text color to include in highlight mark
    const textColor = getTextColor(state);
    const mark = state.schema.marks.highlight?.create({ color, textColor });
    if (!mark) return false;
    if (dispatch) {
      dispatch(state.tr.addMark(from, to, mark));
    }
    return true;
  };
}

/** Set text color on the current selection (as a mark for text, or node attr for xamix/block_xamix). Pass '' to remove. */
export function setTextColor(color: string): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    const { from, to } = state.selection;
    const xamix = getXamixAtSelection(state);
    if (xamix) {
      if (dispatch) {
        dispatch(state.tr.setNodeMarkup(xamix.pos, null, { ...xamix.node.attrs, color: color || null }));
      }
      return true;
    }
    if (!color) {
      if (dispatch) {
        dispatch(state.tr.removeMark(from, to, state.schema.marks.textColor));
      }
      return true;
    }
    const mark = state.schema.marks.textColor?.create({ color });
    if (!mark) return false;
    if (dispatch) {
      // Also update the textColor attribute on any highlight marks in the selection
      const tr = state.tr.addMark(from, to, mark);
      // Check for existing highlight marks and update their textColor
      let hasHighlightUpdate = false;
      state.doc.nodesBetween(from, to, (node) => {
        const highlightMark = node.marks.find((m) => m.type.name === 'highlight');
        if (highlightMark) {
          const newHighlightAttrs = { ...highlightMark.attrs, textColor: color };
          tr.addMark(from, to, state.schema.marks.highlight.create(newHighlightAttrs));
          hasHighlightUpdate = true;
        }
      });
      dispatch(tr);
    }
    return true;
  };
}

/** Get background color of the cell at the current selection, or first selected cell. */
export function getCellBackgroundColor(state: EditorState): string | null {
  if (!isInTable(state)) return null;
  const sel = state.selection;
  let cell: import('prosemirror-model').Node | null = null;
  if (sel instanceof CellSelection) {
    sel.forEachCell((node) => {
      if (!cell) cell = node;
    });
  } else {
    const $cell = cellAround(sel.$from);
    if ($cell) cell = $cell.nodeAfter ?? null;
  }
  if (!cell) return null;
  const bg = (cell.attrs as { backgroundColor?: string | null }).backgroundColor;
  return bg && bg !== '' ? bg : null;
}

/** Set background color on the selected cell(s). When multiple cells are selected, applies to all. */
export function setCellBackgroundColor(color: string): Command {
  return (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
    if (!isInTable(state)) return false;
    const sel = state.selection;
    let tr = state.tr;
    if (sel instanceof CellSelection) {
      sel.forEachCell((node, pos) => {
        tr = tr.setNodeMarkup(pos, null, { ...node.attrs, backgroundColor: color || null });
      });
    } else {
      const $cell = cellAround(sel.$from);
      if (!$cell) return false;
      const cellPos = $cell.pos;
      const cell = state.doc.nodeAt(cellPos);
      if (!cell) return false;
      tr = tr.setNodeMarkup(cellPos, null, { ...cell.attrs, backgroundColor: color || null });
    }
    if (dispatch) dispatch(tr);
    return true;
  };
}
